import { REGEX_COMMON } from '@ricoh/ria.client.core/constants/core/regex';

/** Url extension. */
export class Url extends URL {
  /** @inheritdoc */
  public constructor(url: string | Url = globalThis.location.origin, base: string | URL = globalThis.location.origin) {
    super(url, base);
  }

  /**
   * Check if value is valid URL.
   *
   * @param {unknown} value Value to check.
   *
   * @returns {boolean} True if valid URL, false otherwise.
   */
  public static isValidUrl(value: unknown): boolean {
    if (value instanceof URL) {
      return Url.isValidUrl(value.toString());
    }

    if (typeof value === 'string') {
      // * Validate regex pattern
      if (!REGEX_COMMON.URL.test(value)) {
        return false;
      }

      // * Validate trailing multiple slashes
      if (value.endsWith('//')) {
        return false;
      }

      try {
        return !!new Url(value);
      } catch {
        return false;
      }
    }

    return false;
  }

  /** Redirect. */
  public redirect() {
    globalThis.location.href = this.toString();
  }

  /**
   * Set search params.
   *
   * @template TParams Params.
   * @param {TParams} params Params.
   */
  public setSearchParams<TParams extends Record<string, string>>(params: TParams) {
    Object.entries(params).forEach(([key, value]) => {
      this.searchParams.set(key, value);
    });
  }

  /**
   * Resolve path.
   *
   * @param {string} path Path.
   *
   * @returns {Url} Url.
   */
  public resolve(path: string): Url {
    if (path.startsWith('http') || path.startsWith('/')) {
      return new Url(path, this);
    }

    const resolvedPath = `${this.pathname}/${path}`.replace(/\/+/g, '/');
    return new Url(resolvedPath, this);
  }

  /**
   * Get host.
   *
   * @returns {string} Host.
   */
  public getHost(): string {
    return `${this.protocol}//${this.host}`;
  }

  /**
   * Get path.
   *
   * @returns {string} Path.
   */
  public getPath(): string {
    return `${this.pathname.replace(/\/+/g, '/')}${this.search}`;
  }

  /**
   * Convert to string.
   *
   * @returns {string} URL string.
   */
  public override toString(): string {
    return `${this.getHost()}${this.getPath()}`;
  }
}
